// frontend/src/theme/theme.js
import { alpha, createTheme, createTransitions, responsiveFontSizes } from '@mui/material/styles';
import { deepmerge } from '@mui/utils';
import {
  BUTTON_RADIUS, V2, inkFor, DUR, EASE, FOCUS_RING, hoverOnly,
  TOUCH_TARGET_COARSE_OFF, TOUCH_TARGET_COARSE_QUADRAT_OFF,
  TOUCH_TARGET_COARSE_ACTIVE, TOUCH_TARGET_COARSE_QUADRAT_ACTIVE,
  PRESS_ACTIVE_COARSE, PRESS_TRANSITION,
  MOBILE_BOTTOM_NAV_CLEARANCE,
} from './tokens';

/**
 * v2Palette — the Design-Language v2 semantic scale, resolved for one mode.
 *
 * Owner decision 2026-08-05 (plan §0.1 E1): dark mode resolves through MUI's
 * `palette.mode`, which is what App.js already switches on. The Token-SOT
 * describes a `[data-theme="dark"]` root attribute, but that comes from the
 * mockups — standalone HTML with no MUI. Introducing it here would put two
 * authorities on one boolean. The SOT's canon table stays authoritative for
 * the VALUES; this function is the mechanism.
 *
 * Each tone carries three members, matching the canon:
 *   main  — full-tone text/icon colour
 *   soft  — the calm fill (never a saturated surface: Zurückhaltungs-Regel)
 *   c100  — the FULL border tone (the anti-pattern rule forbids a left bar
 *           on a rounded surface; state is soft fill + full border + chip)
 */
// Chart palette. MANDATED by `pattern-library.md` ("Chart color in
// `Sparkline`: use `theme.palette.primary.main` for single-series trend.
// `theme.palette.chart.*` for multi-series") — and until 2026-09-26 the key
// did not exist. That is why `PriceChart.jsx` carried 37 hex literals and
// `SalesChart.jsx` 14: there was no token to reach for.
//
// 🔴 CATEGORICAL, NOT SEMANTIC. Owner-Entscheid A2, 2026-09-28. Until then
// `chart.series` held six entries lifted from the v2 semantic scale —
// `okText`, `warnText`, `errText` and friends. That was the wrong job for the
// right tokens. A categorical palette answers ONE question: which series is
// this. Reusing the status tones for it invents meaning — a competitor drawn
// in `errText` red reads as a problem, one in `okText` green as a good deal —
// and the chart says nothing of the kind. The two roles that DO carry meaning
// are their own keys now: `ours` (brand) and `median` (neutral).
//
// 🔴 NEW HEXES, deliberately, and only here. `theme/` is the one directory
// Rule 22's gate does not scan: colours are DEFINED here and CONSUMED as
// tokens everywhere else. The comment this replaces ("NO NEW COLOURS, every
// entry is an existing V2 token") described the rule that produced the defect
// above; a categorical ramp cannot be built out of a semantic scale, so it is
// built out of a validated one instead.
//
// Source: the `dataviz` skill's reference palette (references/palette.md), the
// validated default instance of a design-system-agnostic method. Its own rule
// is "run the validator, never eyeball" — so these were re-measured, not
// copied on faith, against THIS app's two chart surfaces: light
// `background.paper` #ffffff and dark #1e293b.
//
//   node scripts/validate_palette.js "<hexes>" --mode light --surface "#ffffff"
//   node scripts/validate_palette.js "<hexes>" --mode dark  --surface "#1e293b"
//
// Result 2026-09-28 (targets: adjacent CVD ΔE >= 8, normal-vision >= 15):
//
//   mode   L band          C floor  adjacent CVD  adjacent normal
//   light  0.433 - 0.764   0.141    9.1           19.6
//   dark   0.529 - 0.670   0.128    8.4           19.3
//
// Both modes also clear with the WRAP-AROUND pair (slot 8 -> slot 1) added to
// the pairlist — the validator's own adjacent list omits it, and the series
// cycling in `PriceChart` can put those two side by side. The eight slots as a
// set FAIL the harder all-pairs gate (light CVD 3.2 / normal 7.1, dark CVD 1.6
// / normal 7.1); that is the palette's documented series cap, not a
// regression — scatter/bubble/small-multiples carry at most three series. The
// FIRST THREE slots clear all-pairs in both modes (light CVD 9.2 / normal
// 24.0, dark CVD 9.4 / normal 20.9), which is the case `SalesChart` uses.
//
// Contrast: three light slots (aqua 2.82:1, yellow 2.17:1, magenta 2.69:1)
// and one dark slot (green 2.96:1) sit under the 3:1 mark target. That is the
// palette's documented "relief" state, not an oversight, and the relief is
// already in the product: identity is never carried by colour alone here —
// both charts name every series in the tooltip, and the OOS badge carries the
// literal text "OOS". Keep it that way; a chart that drops the names and keeps
// the colours would ship three unreadable lines.
//
// ORDER — the slot order IS the CVD-safety mechanism, not decoration. The
// order below is the reference palette's documented default, kept UNCHANGED,
// and that was a measured decision rather than a copy: all 8! = 40320
// orderings were enumerated. 32 clear the adjacent gates in both modes once
// the wrap pair is included; only 8 of those ALSO clear the all-pairs gate for
// their first three slots, and NONE of those 8 puts violet last. Re-ordering
// to push violet — whose light step `#4a3aa7` sits only ΔE 8.3 from the brand
// navy that `ours` uses, the tightest clash in the set — out of reach was
// therefore refuted by measurement: at slot 7 it needs seven simultaneous
// competitors, and the alternative costs the three-series guarantee.
// `ours` is additionally separated by stroke width (2.0 vs 1.6) and by the
// tooltip name, so the clash is a documented limitation, not a defect.
const CHART_SERIES_LIGHT = [
  '#2a78d6', // blue
  '#eb6834', // orange
  '#1baf7a', // aqua
  '#eda100', // yellow
  '#e87ba4', // magenta
  '#008300', // green
  '#4a3aa7', // violet
  '#e34948', // red
];
// The same eight hues stepped for the dark surface — a selected dark set, not
// an automatic flip of the light one.
const CHART_SERIES_DARK = [
  '#3987e5', // blue
  '#d95926', // orange
  '#199e70', // aqua
  '#c98500', // yellow
  '#d55181', // magenta
  '#008300', // green  (mode-invariant: it already sits in the dark band)
  '#9085e9', // violet
  '#e66767', // red
];

const chartPalette = (mode) => {
  const dark = mode === 'dark';
  return {
    series: dark ? CHART_SERIES_DARK : CHART_SERIES_LIGHT,
    // "Our price" — the ONE series that is allowed to mean something, so it
    // takes the brand tone and not a categorical slot. Measured: 10.64:1 on
    // the light paper, 8.11:1 on the dark one.
    ours: dark ? V2.primaryTextDark : V2.primaryText,
    // "Median across competitors" — a statistical reference, not a party, so
    // it takes a neutral and belongs to no hue family at all. `ink70` is the
    // app's existing neutral ink ramp, resolved per mode; composited over the
    // paper it lands on #575d6a light (6.61:1) and #abb3bd dark (6.91:1), and
    // its closest categorical slot is ΔE 15.3 / 16.1 away — at the
    // normal-vision floor, i.e. never mistaken for a series.
    median: dark ? V2.ink70Dark : V2.ink70,
  };
};

const v2Palette = (mode) => {
  const dark = mode === 'dark';
  return {
    ok:    dark ? { main: V2.okTextDark,   soft: V2.okSoftDark,   c100: V2.ok100Dark }
                : { main: V2.okText,       soft: V2.okSoft,       c100: V2.ok100 },
    warn:  dark ? { main: V2.warnTextDark, soft: V2.warnSoftDark, c100: V2.warn100Dark }
                : { main: V2.warnText,     soft: V2.warnSoft,     c100: V2.warn100 },
    err:   dark ? { main: V2.errTextDark,  soft: V2.errSoftDark,  c100: V2.err100Dark }
                : { main: V2.errText,      soft: V2.errSoft,      c100: V2.err100 },
    info:  dark ? { main: V2.infoTextDark, soft: V2.infoSoftDark, c100: V2.info100Dark }
                : { main: V2.infoText,     soft: V2.infoSoft,     c100: V2.info100 },
    agent: dark ? { main: V2.agentDark,    soft: V2.agentSoftDark, c100: V2.agentSoft100Dark }
                : { main: V2.agent,        soft: V2.agentSoft,     c100: V2.agentSoft100 },
    // `brand` is the canon's `primary` row. It is the app's own colour used as
    // a SEMANTIC tone (a selected facet, a brand-owned chip) — not a second
    // primary. Restored 2026-08-05 after PR-A struck it for lacking tokens;
    // the tokens were the thing that was missing, not the row in the canon.
    brand: dark ? { main: V2.primaryTextDark, soft: V2.primarySoftDark, c100: V2.primary100Dark }
                : { main: V2.primaryText,     soft: V2.primarySoft,     c100: V2.primary100 },
    ink: inkFor(mode),
  };
};

/**
 * v2Tone — read one v2 tone off a theme that may not carry the scale.
 *
 * Shared Bausteine can be mounted under a bare `createTheme()` — existing unit
 * tests do exactly that, and so will any consumer who builds a theme by hand.
 * Reading `theme.palette.v2.warn` directly throws there. This resolver falls
 * back to computing the scale from the theme's own mode, so a component
 * degrades to the right colours instead of crashing the tree.
 *
 * @param t     a MUI theme
 * @param key   'ok' | 'warn' | 'err' | 'info' | 'agent' | 'ink'
 */
export const v2Tone = (t, key) => (t?.palette?.v2 ?? v2Palette(t?.palette?.mode || 'light'))[key];

/**
 * chartTone — the chart palette off a theme that may not carry it.
 *
 * Same reason as `v2Tone` above: `PriceChart` and `SalesChart` are mounted by
 * unit tests under a bare `createTheme()`, and reading `theme.palette.chart`
 * directly would throw there. Falls back to computing the palette from the
 * theme's own mode.
 *
 * @param t   a MUI theme
 * @returns   { series: string[8], ours: string, median: string }
 */
export const chartTone = (t) => t?.palette?.chart ?? chartPalette(t?.palette?.mode || 'light');

/**
 * W-13 — the viewport width at which the content minimum actually FITS.
 *
 * Measured, not guessed. The content column is what the grid leaves over
 * after the sidebar and the main box padding:
 *
 *   sidebar             --sidebar-width        280 px   ← der MODUS
 * + main box padding    px: { sm: 2.5 }          40 px
 * + content floor       --content-min           960 px
 * =                                           1280 px
 *
 * 🔴 **Hier stand 240 / 1240 bis zum 2026-10-06.** Der Customize-Modus der
 * Leiste ist 280 px breit (`SIDEBAR_WIDTH_MODUS` in `SidebarAndHeaderBar.jsx`,
 * Begruendung und Rechnung dort); die Schwelle muss dem BREITESTEN Zustand
 * folgen, nicht dem Vorgabezustand. Mit 1240 stand die Seite zwischen 1240 und
 * 1279 px mit geoeffnetem Modus wieder unter einem waagerechten Balken —
 * genau der Defekt, gegen den diese Konstante gebaut wurde.
 *
 * ⚠️ **Was sich dadurch ausserhalb des Modus aendert:** zwischen 1240 und
 * 1279 px bleibt die Inhaltsspalte jetzt fluid statt auf 960 px zu bestehen.
 * Das ist die Richtung, die W-13 ohnehin wollte (kein Balken), und es ist der
 * Preis dafuer, dass die Schwelle eine Konstante ist und kein Ausdruck.
 *
 * Below the threshold the 960 px floor cannot be honoured by any arrangement of
 * those three: the grid track grows past the viewport instead of the content
 * shrinking, which is what produced the horizontal page scrollbar from 600 px
 * up to the old threshold. The rule is therefore gated here rather than at the
 * sm breakpoint (600 px), where it used to start.
 *
 * A media query cannot read a CSS variable, so the sum is spelled out. The
 * three numbers live in three places: the first is `SIDEBAR_WIDTH_MODUS` in
 * `SidebarAndHeaderBar.jsx` (the mode width — `--sidebar-width` and
 * `SIDEBAR_WIDTH` are the 240 px default state and are NOT this number), the
 * second is the `--content-min` custom property in the MuiCssBaseline block
 * below, the third is the `px: { sm: 2.5 }` value on that file's main box.
 * Change one of them and this constant changes with it.
 */
const CONTENT_MIN_VIEWPORT = 1280;

/**
 * modeCssVars — die modusabhaengigen CSS-Variablen, die `buildTheme` auf `:root` schreibt.
 * Drei davon fuehrt `variables.css` auch (andere Werte); dieser Block gewinnt, er steht spaeter
 * im DOM (gemessen 2026-10-04). Was `variables.css` sonst fuehrt (`--glass-blur`, `--appbar-glass-*` u. a.), liest nichts.
 *
 * `:root` traegt sie fuer die ganze App (CssBaseline unten). Ein VERSCHACHTELTES
 * `ThemeProvider` erbt sie aber vom aeusseren Modus, denn `:root` gibt es nur
 * einmal. Der Komponentenkatalog (`/design`) zeigt Hell und Dunkel auf einer
 * Seite und setzt deshalb diese Variablen auf seine Vorschauflaeche — gemessen
 * 2026-10-04: ohne sie lag ein dunkler Outline-Knopf unter 600 px auf dem
 * hellen Glas `rgba(255,255,255,0.62)`, 1,23:1. Die App selbst hat nur ein
 * Theme und war davon nie betroffen.
 *
 * @param mode 'light' | 'dark'
 * @returns {Record<string, string>} CSS-Custom-Properties, direkt in `sx` verwendbar
 */
export const modeCssVars = (mode) => ({
  '--table-head-bg': mode === 'light' ? '#f1f5f9' : '#1e293b',
  // Glass system (T2 filter pills + T3 round icon buttons)
  '--glass-bg': mode === 'light' ? 'rgba(255,255,255,0.62)' : 'rgba(255,255,255,0.08)',
  '--glass-border': mode === 'light' ? 'rgba(200,210,230,0.70)' : 'rgba(255,255,255,0.10)',
  '--glass-shadow': mode === 'light'
    ? '0 2px 8px rgba(15,23,42,0.07), inset 0 1px 0 rgba(255,255,255,0.85)'
    : '0 2px 8px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.06)',
});

export default function buildTheme(mode = 'light') {
  // 1️⃣ Start with MUI default theme
  const baseTheme = createTheme({ palette: { mode } });

  // 2️⃣ Merge your customizations *on top*
  let theme = createTheme(
    deepmerge(baseTheme, {
      palette: {
        ...(mode === 'light'
          ? {
              // Same hex as before, now read from the token so the canon's
              // `primary` row exists exactly once (see V2.primaryText).
              primary: { main: V2.primaryText },
              secondary: { main: '#f59e0b' },
              background: { default: '#f8fafc', paper: '#ffffff' },
              // Design-Language v2 (additive, candidate) — custom palette
              // color for the case-system UI conversion. Consumed as
              // `theme.palette.agent.main` / `sx={{ color: 'agent.main' }}`
              // exactly like a built-in palette key. Does not touch
              // primary/secondary/background above.
              // See docs/concepts/case-workflow-engine/ux/design-language-proposal.md
              agent: { main: V2.agent, light: V2.agentSoft, contrastText: '#fff' },
            }
          : {
              primary: { main: V2.primaryTextDark },
              secondary: { main: '#fbbf24' },
              background: { default: '#0f172a', paper: '#1e293b' },
              agent: { main: V2.agentDark, light: V2.agentSoftDark, contrastText: '#1e1b4b' },
              // 🔴 MUI-`error` an E4 gebunden (2026-10-04, Katalog Phase 1). Bis
              // hierher galt im Dunkelmodus MUI-Standard `#f44336`: als Text auf
              // `paper` 3,97:1, als Flaeche unter weisser Schrift 3,68:1 — beides
              // unter AA, gemessen von axe in `/design`. `augmentColor` statt eines
              // blossen `main`: deepmerge liesse `light/dark/contrastText` der Basis
              // stehen, und `#fff` auf `#fca5a5` waere 1,9:1. So rechnet MUI die
              // Schrift (dunkel) und den Hover-Ton neu aus demselben Token.
              error: baseTheme.palette.augmentColor({ color: { main: V2.errTextDark }, name: 'error' }),
            }),
        // Design-Language v2 semantic scale, resolved for THIS mode. Read it
        // as `theme.palette.v2.warn.soft` etc. Shared Bausteine (Pill,
        // AgingBar, ProvenanceMark, TierBadge) resolve their `tone` prop
        // through here instead of branching on `isDark` themselves — which
        // is what SituationHeader.jsx:106-113 had to do while the dark
        // siblings were missing, and why hex literals ended up in it.
        v2: v2Palette(mode),
        // Multi-series chart colours. See chartPalette above for why this key
        // exists — it is the ONE place new hexes are allowed, deliberately.
        chart: chartPalette(mode),
      },

      typography: {
        // 🔴 htmlFontSize and fontSize are INERT — measured 2026-08-09, not derived.
        // `buildTheme` deep-merges this object over `baseTheme` (:61), which
        // `createTheme` has ALREADY expanded into concrete per-variant sizes
        // (caption `0.75rem`, body1 `1rem`, …). Those variant objects survive the
        // merge, so the `coef = fontSize / 14` recomputation never reaches them.
        // Proof: raising fontSize 13 → 14 and dumping every `font-size` in
        // `document.styleSheets` before and after produced byte-identical rem sets.
        // Left at 13 deliberately — changing it does nothing, and REMOVING it would
        // be a silent behaviour change if the merge order is ever fixed.
        // Owner decision E3 ("eine Skala") is accepted but NOT delivered by PR-B:
        // docs/backlog-auto-repair/2026-08-09-e3-eine-skala-braucht-buildtheme-umbau.md
        htmlFontSize: 14,
        fontSize: 13,
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        h1: { fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: 700 },
        h2: { fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: 700 },
        h3: { fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: 600 },
        h4: { fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: 600 },
        h5: { fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: 600 },
        h6: { fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: 600 },
        subtitle1: { fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: 600 },
        subtitle2: { fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: 600 },
      },

      shape: { borderRadius: BUTTON_RADIUS },

      transitions: {
        // 🔴 K-T1 (UI-Durchgang 2, Teil 6, 2026-10-09) — die zwei Haus-Dauern
        // speisen MUI, statt dass jede Flaeche ihre eigene Zahl mitbringt.
        //
        // `standard` faellt damit von 400 auf 200, und das ist die
        // WIRKUNGSVOLLSTE Zeile dieses Durchgangs: MUI liest `standard` als
        // VORGABE fuer jeden Uebergang ohne eigene Dauer
        // (`@mui/material/esm/Collapse/Collapse.js:143`, `timeout =
        // duration.standard`), und `short`/`long` ebenso. Sichtbar wird es an
        // jedem aufklappenden Bereich.
        //
        // ⚠️ NUR diese drei Schluessel. `createTheme` mischt tief, `shortest`,
        // `shorter`, `complex`, `enteringScreen` und `leavingScreen` tragen die
        // MUI-Vorgaben und werden hier NICHT angefasst. Sie „wiederherzustellen",
        // obwohl sie stehen, waere keine Wiederherstellung, sondern eine
        // Aenderung der MUI-Timings von Dialog, Drawer, Fade und Slide.
        duration: { short: DUR.ui, standard: DUR.ui, long: DUR.panel },
        easing: {
          // Die drei Kurven mit Hausnamen; `sharp` hat keinen (`EASE` fuehrt
          // decelerate/accelerate/standard/drawer) und behaelt seinen Wert.
          easeInOut: EASE.standard,
          easeOut:   EASE.decelerate,
          easeIn:    EASE.accelerate,
          sharp:     'cubic-bezier(0.4, 0, 0.6, 1)',
        },
      },

      components: {
        // 🌍 Global layout tokens (width system)
        MuiCssBaseline: {
          styleOverrides: (theme) => `
            @keyframes fadeInUp {
              from { opacity: 0; transform: translateY(var(--fade-distance, 24px)); }
              to   { opacity: 1; transform: translateY(0); }
            }
            /* 🔴 M2 §8.2 (2026-10-16): -webkit-text-size-adjust: 100% — ohne
               die Eigenschaft blaeht iOS Safari Text beim Drehen in breiten
               Bloecken SELBSTTAETIG auf (Dynamic Type). Im Baum stand sie
               nirgends (gemessen: 0 Treffer ueber frontend/). In Chromium ist
               die Wirkung nicht messbar — Deklaration plus Handtest; der PWA ist
               per manifest.json portrait-gesperrt, es betrifft also den
               Browser-Tab. */
            html { font-size: 14px; color-scheme: ${mode}; -webkit-text-size-adjust: 100%; }
            /* 1rem = 14px, deckungsgleich mit FS.base. Was 0.92rem (12.88px)
               until 2026-08-09 (P1/PR-B). Unlike typography.fontSize above this
               lever DOES work — measured body 12.88px → 14px. */
            body { font-size: 1rem; }

            /* iOS overscroll (rubber-band) reveals the <html> element background,
               not <body>. MUI CssBaseline only sets background on <body>, so
               this rule has no specificity conflict and needs no !important.
               On mobile the AppBar + wrapper are always #12121a — match it here
               so the overscroll zone doesn't flash a different colour. */
            @media (max-width: 599px) {
              html { background-color: #12121a; }
            }
            :root {
              /* 🔴 Die Seiten-Deckel-Variable (die fruehere 1440er CSS-Variable
                 am Container) ist in G0b.5 (2026-10-13) ENTFALLEN — NW5.2. Der
                 Deckel lebt in den Breitenmustern (CONTENT_WIDTH,
                 theme/tokens.js), die PageContainer/ViewHeader lesen, nicht mehr
                 in einer CSS-Variable am MuiContainer-Override. Zwei Deckel auf
                 derselben Achse waren der Ursprung des Widerspruchs zwischen der
                 1440er-Vorgabe und dem 1280er Muster
                 (docs/forge-ux-redesign/concepts/inhaltsbreite.md §5.3). */
              --content-min: 960px;
              --content-gutter: 24px;
              --sidebar-width: 240px;
              /* 🔴 R16-B1 (UI2 Teil 9, Z-1a, Nachbesserung 4): die Hoehe des
                 Ausfall-Banners. Der Banner misst sie selbst und setzt sie am
                 \`documentElement\` (\`ServiceOutageBanner.jsx\`,
                 \`BANNER_HOEHE_VAR\`); hier steht nur der Gruen-Wert, damit die
                 Variable auch VOR der ersten Messung aufloest. \`0px\` heisst:
                 die Schale steht unveraendert. */
              --pog-banner-hoehe: 0px;
              /* Modusabhaengige Variablen — Quelle ist \`modeCssVars\` oben. */
              ${Object.entries(modeCssVars(mode)).map(([k, v]) => `${k}: ${v};`).join('\n              ')}
            }

            /* 🔴 R16-B1 — der Banner VERSCHIEBT die Schale, statt sie zu
               verdecken. Er liegt \`fixed\` am oberen Rand (Ebene 1299, ueber der
               Kopfleiste 1202) und deckte deren Bedienelemente zu: ein
               Tastaturnutzer tabbt auf ein vollstaendig verdecktes Ziel
               (WCAG 2.2 SC 2.4.11, AA). Bis hierher war die Ebene entschieden
               (E-10), die FOLGE aber nie gemessen.

               Drei Regeln, EINE Variable — die Kopfleiste, das feste
               Seitenleisten-Papier und der Inhaltsfluss ruecken um genau die
               gemessene Hoehe nach. Im Gruenen ist sie \`0px\`, also aendert sich
               nichts. \`!important\` ist noetig und nicht bequem: \`sx\` auf
               \`PaperProps\`/\`position\` erzeugt gleich spezifische Klassen, die
               SPAETER eingespielt werden — ohne das Flag gewaenne die
               Komponente. Dieselbe Begruendung wie beim iOS-Zoom-Riegel unten.

               \`body\` traegt den Inhaltsfluss: \`main\` liegt im normalen Fluss,
               und \`position: fixed\` haengt am Viewport, nicht am \`body\` — die
               zwei festen Flaechen brauchen darum ihre eigenen Zeilen.

               🔴 **Und die Seitenleiste wird GENAU benannt, nicht als
               \`.MuiDrawer-paper\`.** Das Repo traegt **ueber zwanzig**
               \`anchor="bottom"\`-Drawer/SwipeableDrawer (ActionSheet, MobileSheet,
               BottomSheetSelect, POStatusBar, RMANewCaseWizardModal …). Ein
               \`top\` auf dem PAPER zwaenge jede dieser Bodenflaechen von oben
               nach unten auf die volle Hoehe — aus einem Inhaltsblatt wuerde
               ein Vollbild. \`paperAnchorDockedLeft\` trifft nur den
               \`variant="permanent"\`-Drawer, und davon gibt es im Baum genau
               einen (die Seitenleiste, \`SidebarAndHeaderBar.jsx:772\`). */
            body { padding-top: var(--pog-banner-hoehe); }
            .MuiAppBar-positionFixed { top: var(--pog-banner-hoehe) !important; }
            .MuiDrawer-paperAnchorDockedLeft,
            .MuiDrawer-paperAnchorDockedRight {
              top: var(--pog-banner-hoehe) !important;
              height: calc(100vh - var(--pog-banner-hoehe)) !important;
              max-height: calc(100vh - var(--pog-banner-hoehe)) !important;
            }

            /* Keyboard focus — visible ring for accessibility.
               🔴 The colour comes from the PALETTE, not from two hex literals
               (K-FR, 2026-10-10). theme.palette.primary.main IS the value
               FOCUS_RING.farbe names, so this rule and the focusRing helper
               resolve to the same colour by construction — one source, not two.
               Width and offset come from the token as well.
               ⚠️ Keine Backticks in diesem Kommentar: er steht INNERHALB eines
               Template-Literals, und ein Backtick beendet es (Parserfehler). */
            *:focus-visible {
              outline: ${FOCUS_RING.breite}px solid ${theme.palette.primary.main};
              outline-offset: ${FOCUS_RING.aussen}px;
              border-radius: 4px;
            }

            /* Clickable table rows */
            .MuiTableRow-hover { cursor: pointer; }

            @media (prefers-reduced-motion: reduce) {
              *, *::before, *::after {
                animation-duration: 0.01ms !important;
                transition-duration: 0.01ms !important;
              }
              /* 🔴 W-09: der animation-iteration-count-Riegel steht EINMAL — in
              theme/variables.css (bedingungslos ueber index.js geladen, gilt
              also auch ohne App-Theme). Er stand bis zum 2026-10-04 auch hier,
              wortgleich, und war damit genau die Doppelung, die dieser PR sonst
              ausraeumt (R16-A W4). Hier NICHT wiederholen. */
            }

            /* Prevent iOS Safari from zooming in when focusing an input.
               Safari zooms whenever the focused input's computed font-size < 16px.
               Using !important here because MUI component-level sx can override
               theme styleOverrides, so the global rule is the only safe anchor. */
            @media (max-width: 599px) {
              input:not([type="checkbox"]):not([type="radio"]):not([type="range"]):not([type="file"]),
              textarea,
              select {
                font-size: 16px !important;
              }
            }
          `,
        },

        // 🧱 Containers: kein Deckel mehr — nur noch Polster und Boden
        //
        // 🔴 G0b.5 (2026-10-13): der Override **deckelt NICHT mehr**. Bis hierher
        // stand hier `maxWidth: 'var(…)'` auf die 1440er Seiten-Deckel-Variable —
        // auf JEDER `MuiContainer` — und zog alle Varianten per `!important` auf
        // dieselbe Zahl: der zweite Deckel neben dem Muster-Deckel des
        // `PageContainer` und damit der 1440/1280-Widerspruch
        // (`inhaltsbreite.md` §5.3, NW5.2:804). Der Deckel lebt jetzt in
        // `CONTENT_WIDTH` (`theme/tokens.js`) und wird von
        // `PageContainer`/`ViewHeader` gesetzt; `PageContainer` reicht
        // `maxWidth={false}` durch.
        //
        // 🔴 **Das POLSTER bleibt** (NW5.3:805, Grill W2): `paddingLeft/Right:
        // var(--content-gutter)`. Wer den Override „ganz raeumt", nimmt den
        // Puffer mit — und der `ViewHeader` daneben sitzt auf jeder Seite 24 px
        // links der Karten (`component-registry.md` §ViewHeader). `--content-min`
        // und seine Schwelle bleiben ebenfalls: sie verhindern den Seitenbalken
        // zwischen 600 und 1280 px (NW5.5:807) und haben mit dem Deckel nichts
        // zu tun.
        MuiContainer: {
          defaultProps: { maxWidth: false },
          styleOverrides: {
            root: {
              maxWidth: '100%',
              paddingLeft: 'var(--content-gutter)',
              paddingRight: 'var(--content-gutter)',
              marginLeft: 'auto',
              marginRight: 'auto',
              width: '100%',
              // Desktop: enforce the minimum width ONLY where it fits.
              // Between 600 and 1239 px the column stays fluid — the sidebar
              // plus a forced 960 px floor is wider than the viewport there,
              // which is exactly what pushed the horizontal scrollbar.
              // See CONTENT_MIN_VIEWPORT for the arithmetic.
              [baseTheme.breakpoints.up(CONTENT_MIN_VIEWPORT)]: {
                minWidth: 'var(--content-min)',
              },
              [baseTheme.breakpoints.down('sm')]: {
                minWidth: 0,
                paddingLeft: '16px',
                paddingRight: '16px',
              },
            },
            // 🔴 G0b.5 (2026-10-13): die sechs Varianten-Regeln
            // (`maxWidthXs`…`fixed`) sind ENTFALLEN. Sie zogen jede
            // `maxWidth`-Variante per `!important` auf 1440 — derselbe zweite
            // Deckel wie `root` darueber. Wer eine Variante braucht, nimmt ein
            // Muster (`PageContainer width=…`); die Namen sind damit keine
            // Deckel-Quelle mehr (`inhaltsbreite.md` §5.3, `:712-713`).
          },
        },

        // 📦 Tables: borderless header — no fill, uppercase caption style
        MuiTableCell: {
          styleOverrides: {
            root: { paddingTop: 8, paddingBottom: 8 },
            sizeSmall: { paddingTop: 8, paddingBottom: 8 },
            head: {
              backgroundColor: 'transparent !important',
              color: mode === 'light' ? 'rgba(0,0,0,0.45)' : 'rgba(255,255,255,0.45)',
              fontWeight: 600,
              fontSize: '0.7rem',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              borderBottom: `2px solid ${mode === 'light' ? '#e2e8f0' : '#334155'}`,
            },
          },
        },
        MuiTableHead: {
          styleOverrides: {
            root: { backgroundColor: 'transparent' },
          },
        },

        // 🧭 Tooltips
        //
        // 🔴 K-T2 (UI-Durchgang 2, Teil 6, 2026-10-09) — hier wird KEINE
        // Verzoegerung gesetzt. Der Befund M-25 hiess „Tooltip OHNE
        // Verzoegerung", also das FEHLEN einer Hausvorgabe — nicht „der Tooltip
        // erscheint zu frueh". MUI's Vorgabe bleibt deshalb unangetastet
        // (Einschaltverzoegerung 100 ms, Ausschaltverzoegerung 0 in
        // `@mui/material` 7.3.5; die Schluesselnamen samt Fundstelle stehen im
        // PR-Text, nicht hier — eine Zahl in einer Anweisungsflaeche altert von
        // selbst, und Abnahme 2 des Bauplans zaehlt das Vorkommen des
        // Schluesselnamens in `frontend/src`).
        //
        // 🔴 Und es gibt auch keine „228 Einzelentscheidungen" zu ersetzen:
        // gemessen traegt der GANZE Baum GENAU EINE Verzoegerung —
        // `pages/VendorReportView.jsx:103`, 200 ms, an einer Hilfe-Schaltflaeche.
        // Ein Theme-Wert von 300 ms haette damit ALLE 228 Tooltips um Faktor 3
        // verlangsamt, ohne gemessenen Grund. Die eine Ausnahme bleibt darum
        // stehen; wer sie fuer ueberfluessig haelt, braucht einen Nutzerbefund,
        // dass Tooltips zu FRUEH erscheinen.
        //
        // 🔴 `disableTouchListener` wird ebenfalls NICHT gesetzt: MUI's
        // Langdruck-Verhalten auf dem Telefon bleibt, wie es ist. Die
        // Touch-Seite traegt eine REGEL, keinen Theme-Wert — „Tooltip traegt
        // nur Zusatzinformation; was zum Verstaendnis noetig ist, steht
        // sichtbar" (Bauplan Teil 6 §4 F-2 = A). Wo der Tooltip das Einzige
        // ist, wird die Stelle umgebaut, nicht das Theme verstellt.
        MuiTooltip: {
          styleOverrides: {
            tooltip: {
              backgroundColor: mode === 'light' ? '#333' : '#e0e0e0',
              color: mode === 'light' ? '#fff' : '#000',
              fontSize: '0.875rem',
              padding: 8,
              borderRadius: 4,
              maxWidth: 260,
            },
            arrow: { color: mode === 'light' ? '#333' : '#e0e0e0' },
          },
        },

        // 🔘 Buttons — three-tier system
        //
        //   T1  variant="contained"  → solid filled, primary color (main CTA)
        //                              Unchanged on mobile — filled = high intent.
        //
        //   T2  variant="outlined"   → glass pill on mobile.
        //                              These are secondary actions: "Inbox", "Reports",
        //                              "CSV", sub-view navigators, contextual actions.
        //                              Desktop: keeps normal outlined border style.
        //
        //   T3  variant="text"       → minimal, no border.
        //                              Rare: low-emphasis in-content links.
        //
        // Rule: contained = one clear primary action per context.
        //       Everything else uses outlined (→ glass on mobile) or text.
        //       Never use outlined+color="error/warning" for decoration —
        //       semantic colors are reserved for destructive / alert actions.

        // W-02 — one default for every control that declares no size.
        // An explicit `size` still wins; only the silent fall-through to
        // MUI's `medium` is removed. 475 controls across the four
        // components below declare no size (Button 280, IconButton 18,
        // TextField 108, Select 69) — measured with a JSX-aware scanner,
        // not by regex. Rollback is a single-commit revert of this file.
        MuiButton: {
          // K-T2 (UI-Durchgang 2, Teil 6, 2026-10-09) — gefuellte Knoepfe tragen
          // KEINEN Schlagschatten. `disableElevation: true` nimmt MUI's
          // Schattenstufe 2 (Ruhe) und 4 (`:hover`) vom `contained`-Knopf.
          // Geprueft wird das im Browser (`tests/design/ui2-k-t2-zustand.spec.ts`):
          // `boxShadow` ist `none`, im Ruhezustand UND unter dem Zeiger.
          //
          // ⚠️ Die Stufen stehen hier als ZAHLEN, nicht als MUI-Ausdruck, und
          // das ist Absicht: Abnahme 1 des Bauplans zaehlt das Vorkommen des
          // MUI-Schatten-Ausdrucks in DIESER Datei und verlangt 0 — das Theme
          // definiert keine eigene Schattenskala. Der Grep liest TEXT, nicht
          // Absicht: ein Kommentar, der den Ausdruck nennt, faerbt ihn rot,
          // obwohl die Zusage haelt (dieselbe Bauform wie Regel 22 §„reads
          // TEXT, not code").
          //
          // 🔴 Der Kanon verlangt das seit je: `design/demos.jsx:69` fuehrt
          // `buttonCanon = { variant: 'contained', size: 'small',
          // disableElevation: true }` — nur hat ihn bis heute KEIN Knopf
          // geerbt, weil die Vorgabe fehlte. Die gefuellten Knoepfe folgen ihm
          // jetzt ueber das Theme statt je Aufruf.
          //
          // 🔴 Und der Schatten LEckt wirklich, nicht nur kosmetisch:
          // `rma/components/CaseCtaButton.jsx:126-135` musste ihn am eigenen
          // `ButtonGroup` einzeln abschalten, weil MUI's Schattenstufe 2 dort
          // als weisser Umriss HINTER der Pille stand — „looks like a second
          // taller button ghosting the real one" (RMA-2026-0404 / case 1410).
          //
          // Ein ausdrueckliches `disableElevation={false}` an einem Aufruf
          // gewinnt weiterhin; die Vorgabe ist ein Rueckfall, keine Sperre.
          defaultProps: { size: 'small', disableElevation: true },
          styleOverrides: {
            root: {
              borderRadius: BUTTON_RADIUS,
              textTransform: 'none',
              whiteSpace: 'nowrap',
              minWidth: 'auto',
              // iOS HIG: 44px minimum touch target on mobile
              [baseTheme.breakpoints.down('sm')]: {
                minHeight: 44,
              },
              // Welle 4 (Karte 6): derselbe Boden auf grobem Zeiger ab `sm` — ein
              // Tablet ist breit und hat trotzdem keinen Hover. Ohne die Zeile greift
              // oberhalb von `sm` KEINE der drei 44-px-Wurzeln; ein schlichter Button
              // misst dort seine natürliche Hoehe (Karte 6 §1.9).
              // 🔴 K-T1-Nachbesserung 3 (2026-10-09) — der Druck steht jetzt in
              // ZWEI Teilen, und das ist die Hausform: `transform` in der
              // EIGENEN `transition` (unten, aus `PRESS_TRANSITION`), der
              // `:active`-Ausschlag aus der transition-FREIEN Konstante hier.
              // Bis zur Nachbesserung brachte `TOUCH_TARGET_COARSE_PRESS` den
              // `transition` MIT — und ersetzte damit auf dem groben Zeiger
              // MUIs eigene Farb-/Schatten-/Rahmen-Uebergaenge (`transition` ist
              // keine additive Eigenschaft). Gemessen: grob stand danach nur
              // noch `transform 0.12s` statt der vollen Liste (fein trug der
              // Defektstand vier, mit `color`). Der grobe Zeiger ist die eine Achse,
              // an der Boden UND Druck haengen — EIN Spread, weil ein zweiter
              // `'@media (pointer: coarse)'`-Schluessel im selben Objektliteral
              // den ersten ersetzte.
              ...TOUCH_TARGET_COARSE_ACTIVE,
              // MUIs WIRKSAME Liste (Variante `loadingPosition: 'center'`,
              // Vorgabe `'center'`) — DREI, nicht vier. Fein inert, grob
              // traegt sie den Druck; sonst blendet jeder Textfarbwechsel mit.
              transition: mitDruck(
                baseTheme.transitions.create(
                  ['background-color', 'box-shadow', 'border-color'],
                  { duration: DUR.ui },
                ),
              ),
              // 🔴 Dichte Kontexte (Tabellenzelle, Tabellenfuss, `Toolbar dense`) nehmen
              // NUR den neuen Boden zurueck. Die Ruecknahme gilt nur `up('sm')` — unter
              // 600 px traegt sie KEINE Deklaration und kann den Telefon-Boden deshalb
              // nicht schlagen; ohne das Gatter stuende `auto` (2 Klassen) hinter dem
              // Telefon-Boden (1 Klasse) und gewaenne.
              // 🔴 Der `down('sm')`-Boden darunter ist heute WIRKUNGSLOS (Regel-15-
              // Befund D, 2026-10-08). Er stand hier mit der Begruendung, sonst verloere
              // eine Telefon-Tabelle auf grobem Zeiger ihre 44 px — die traegt nicht:
              // unter 600 px nimmt ihn niemand zurueck, und die 44 px tragen ohnehin
              // `root[down('sm')]` und `TOUCH_TARGET_COARSE` (beide 1 Klasse, beide 44).
              // Die Zeile bleibt stehen, weil ein Rueckbau keine Wirkung aendert.
              // `Toolbar dense` ist Vorsorge — es gibt keinen Aufrufer im Baum (§4.1a).
              '.MuiTableCell-root &, .MuiTablePagination-root &, .MuiToolbar-dense &': {
                [baseTheme.breakpoints.down('sm')]: { minHeight: 44 },
                [baseTheme.breakpoints.up('sm')]: { ...TOUCH_TARGET_COARSE_OFF },
              },
            },

            // T2 — glass pill on mobile
            outlined: {
              [baseTheme.breakpoints.down('sm')]: {
                borderRadius: 99,
                backgroundColor: 'var(--glass-bg)',
                backdropFilter: 'saturate(160%) blur(10px)',
                WebkitBackdropFilter: 'saturate(160%) blur(10px)',
                borderColor: 'var(--glass-border)',
                boxShadow: 'var(--glass-shadow)',
                '&:hover': {
                  backgroundColor: 'var(--glass-bg)',
                  borderColor: 'var(--glass-border)',
                },
                // 🔴 K-T1: hier stand die Druckregel mit dem alten Ausschlag
                // 0,94 unter `down('sm')` — also nach BREITE gegatet. Sie ist an
                // `root` gezogen und dort auf `(pointer: coarse)` gestellt
                // (`...TOUCH_TARGET_COARSE_ACTIVE`); ein schmales Desktop-Fenster
                // hat einen feinen Zeiger und drueckt seitdem nicht mehr, ein
                // breiter Touch-Laptop dagegen schon.
              },
            },

            // T1 — contained: FILLED, not a pill, ab `sm` (E-W1). Unter `sm`
            // bleibt die Pille (99) wie bei T2 daneben; bis 2026-10-06 trug
            // `contained` überall 99 (`ba46ea96a4`, kein Owner-Entscheid).
            contained: {
              [baseTheme.breakpoints.down('sm')]: {
                borderRadius: 99,
              },
            },

            // size="small" — tighter horizontal padding so short labels ("Edit",
            // "CSV", "Add") don't look like bloated circles on mobile.
            // Vertical padding stays at 44px via minHeight (touch target requirement).
            sizeSmall: {
              [baseTheme.breakpoints.down('sm')]: {
                paddingLeft: 14,
                paddingRight: 14,
              },
            },

            // size="medium" (default) — comfortable but not excessive
            sizeMedium: {
              [baseTheme.breakpoints.down('sm')]: {
                paddingLeft: 18,
                paddingRight: 18,
              },
            },
          },
        },

        // K-T2-Nachtrag (2026-10-09, Orchestrator-Messung) — die HUELLE einer
        // Knopfgruppe traegt denselben Schatten, und `MuiButton.defaultProps`
        // erreicht sie NICHT: `defaultProps` wirkt je Komponente, nicht ueber
        // den Baum. Ohne diesen Block waere die Abschaltung oben fuer jede
        // Knopfgruppe wirkungslos.
        //
        // 🔴 GEMESSEN (Orchestrator, 2026-10-09, CRA 3005, 1440 px) auf
        // `/products`: der primaere Kopfknopf ist ein `SplitButton`
        // (`components/common/SplitButton.jsx:159`), also eine `ButtonGroup`.
        // Ihre KNOEPFE tragen schon auf master keinen Schatten
        // (`boxShadow: none, none`) — die Schattenstufe 2 sitzt auf der HUELLE
        // (`.MuiButtonGroup-root.MuiButtonGroup-contained`). Genau darum ist
        // dieser Block noetig und nicht Kosmetik: eine Probe, die dort den
        // Knopf misst, waere schon auf master gruen, also vakuoes (Regel 17
        // §1). Gemessen wird die Huelle.
        //
        // 🔴 Der Leck-Fall ist belegt, nicht vermutet:
        // `rma/components/CaseCtaButton.jsx:135` schaltet ihn am eigenen
        // `ButtonGroup` von Hand ab, weil die Schattenstufe 2 dort als weisser
        // Umriss hinter der Pille stand („looks like a second taller button
        // ghosting the real one", RMA-2026-0404 / case 1410). Diese Zeile
        // zieht dieselbe Abschaltung fuer ALLE Gruppen nach.
        //
        // ⚠️ Die Vorgabe an `MuiButton` bleibt daneben stehen: sie traegt die
        // Einzelknoepfe, diese hier die Huelle. Die Gruppe gibt
        // `disableElevation` an ihre Knoepfe weiter — beide Wege sind gewollt.
        //
        // Ein ausdrueckliches `disableElevation={false}` an einem Aufruf
        // gewinnt weiterhin; die Vorgabe ist ein Rueckfall, keine Sperre.
        MuiButtonGroup: {
          defaultProps: { disableElevation: true },
        },

        // 🔴 K-T1 — die Vorgabe fuer jeden aufklappenden Bereich.
        //
        // 🔴 K-T1-Nachbesserung 3 (2026-10-09) — hier stand, `Collapse` lese
        // „die Theme-Dauer", die Zeile sei damit heute „WIRKUNGSGLEICH". Das
        // ist FALSCH, und die Verwechslung ist teuer: sie verleitet dazu, die
        // Zeile als ueberfluessig zu loeschen.
        //
        // `Collapse` liest seinen Vorgabe-`timeout` NICHT aus dem Theme,
        // sondern aus MUI's STATISCHEM Modul-Import (`Collapse.js:13`
        // `require('../styles/createTransitions')`, `Collapse.js:143`
        // `timeout = duration.standard` = MUI's eigene 300 ms). Der Theme-Wert
        // erreicht ihn nur ueber DIESE Zeile.
        // GEMESSEN (Orchestrator, 2026-10-09, CRA 3005): die Zeile testweise
        // entfernt → C.1 rot, `Expected "200ms", Received "300ms"`.
        //
        // Damit ist sie der EINZIGE Griff fuer die 46 Aufrufe im Baum, die gar
        // kein eigenes `timeout` setzen (Messmethode: `<Collapse` in
        // `frontend/src`, ohne Testdateien, Kommentarzeilen ausgenommen,
        // mehrzeilige Tags mitgezaehlt, ohne `timeout` im Tag → 46 von 59).
        //
        // ⚠️ Die KLASSE daneben liest das Theme SEHR WOHL (`Collapse.js:47`,
        // `transition: theme.transitions.create('height')`) — sie folgt der
        // reparierten `transitions.create`-Zeile (Schritt 4 weiter unten). Die
        // zwei Werte haben also VERSCHIEDENE Quellen, und nur einer von beiden
        // haengt am Theme.
        //
        // ⚠️ Was das NICHT ist: eine neue Zahl. Wer sie aendert, aendert die
        // Dauer JEDES aufklappenden Bereichs — das ist der Grund, aus dem sie
        // aus `DUR` kommt und nicht als Literal dasteht.
        MuiCollapse: {
          defaultProps: { timeout: DUR.ui },
        },

        // ── Tabs / ToggleButtons — Satzschreibung ──────────────────────
        // MUI-Default ist `uppercase` für BEIDE; die DOM-Texte standen längst
        // in Satzschreibung (`MarketLab.jsx:532-537`), nur das CSS schrie.
        // `MuiTableCell head` behält sein Uppercase bewusst.
        MuiTab: {
          styleOverrides: {
            // K-T1: der Reiter ist die fuenfte Knopfflaeche. Er traegt KEINEN
            // 44-px-Boden aus dem Theme (das tun nur `MuiButton`,
            // `MuiIconButton`, `MuiToggleButton` und der klickbare Chip),
            // deshalb hier `PRESS_ACTIVE_COARSE` allein.
            // 🔴 K-T1-Nachbesserung 3/4 (2026-10-09): `MuiTab` fuehrt keinen
            // eigenen Uebergang (`@mui/material 7.3.5`: `Tab.js` ohne
            // `transition`, gemessen) — der `transform` steht trotzdem HIER,
            // damit alle fuenf Wurzeln dieselbe Bauform tragen und eine spaetere
            // MUI-Fassung den Druck nicht still ersetzt.
            root: { textTransform: 'none', transition: PRESS_TRANSITION, ...PRESS_ACTIVE_COARSE },
          },
        },

        MuiToggleButton: {
          styleOverrides: {
            root: {
              textTransform: 'none',
              // Welle 4 (Karte 6) hat den Boden an `MuiButton` und
              // `MuiIconButton` gehaengt; `MuiToggleButton` ist der dritte
              // KNOPF und stand daneben ohne ihn. Die Zeiger-Abfrage gehoert
              // dazu (`pattern-library.md:47`): ein Tablet ist breit und hat
              // trotzdem keinen Hover.
              // 🔴 K-T1-Nachbesserung 3/4 (2026-10-09): transition-FREI, wie die
              // vier anderen Wurzeln — `MuiToggleButton` fuehrt keinen eigenen
              // Uebergang (`@mui/material 7.3.5`: `ToggleButton.js` ohne
              // `transition`, gemessen); `transform` in der EIGENEN Liste.
              ...TOUCH_TARGET_COARSE_ACTIVE,
              transition: PRESS_TRANSITION,
              // 🔴 Dichte Kontexte nehmen ihn zurueck — dieselben drei wie bei
              // `MuiButton` (C1, Regel-15-Befund 1 vom 2026-10-08). Ohne diese
              // Zeile traegt ein Segment-Umschalter in einer Tabellenzelle auf
              // grobem Zeiger 44 px, waehrend der Knopf daneben bei 32 px steht.
              // Nur `up('sm')`: `MuiToggleButton` hat — anders als `MuiButton`
              // — KEINEN `down('sm')`-Boden, den die spezifischere Regel
              // sonst verdecken muesste.
              '.MuiTableCell-root &, .MuiTablePagination-root &, .MuiToolbar-dense &': {
                [baseTheme.breakpoints.up('sm')]: { ...TOUCH_TARGET_COARSE_OFF },
              },
            },
          },
        },

        // 🔴 F3 (UI-Durchgang 2, Teil 2, 2026-10-07) — die Groesse der
        // Segment-Umschalter steht im Theme, nicht an 19 Aufrufstellen.
        // Gemessen am 2026-10-07: 19 `<ToggleButtonGroup>` in `frontend/src`,
        // ALLE 19 tragen schon `size="small"` — die Vorgabe ist deshalb heute
        // wirkungsgleich und eine Sicherung gegen den Rueckfall, keine
        // Aenderung. Sie steht hier, weil die naechste Gruppe sonst wieder
        // still auf MUI's `medium` faellt (dieselbe Begruendung wie bei
        // `MuiFormControl`/`MuiTextField`/`MuiSelect` oben).
        MuiToggleButtonGroup: {
          defaultProps: { size: 'small' },
        },

        // 🔴 F3 — dieselbe Vorgabe fuer Chips, und dort ist sie eine
        // AENDERUNG. Gemessen am 2026-10-07 ueber `frontend/src` (ohne
        // Testdateien, JSX-bewusster Scan): **rund zwei Dutzend** `<Chip>`
        // ohne `size` — sie fielen still auf MUI's `medium` (32 px). Die
        // Uneinheitlichkeit war der Befund, nicht die Groesse.
        //
        // 🔴 HIER STEHEN ABSICHTLICH KEINE FESTEN ZIFFERN MEHR (Regel-16-Befund
        // W1, 2026-10-08). Die drei Flaechen, die „464 / 436 / 0 / 23"
        // zitierten, reproduzierten die Zahl nicht: ein klammerungssicherer
        // JSX-Scan zaehlt am Kopf 467 gesamt / 441 mit `size` / 437 `small` /
        // 2 `medium` (die zwei Zug-Pillen) / 2 dynamisch (`size={size}`:
        // `recommendations/OverrideBadge.jsx:72` und
        // `common/CartAwarePriceBadge.jsx:124`, beide mit Vorgabe `'small'`) /
        // 26 ohne — die Groessenordnung des Zweigs stimmt, die exakten Ziffern
        // weichen um 1–5 ab. 🔴 437 + 2 + 2 = 441, die Zerlegung geht auf
        // (Regel-16-Befund W8, 2026-10-08: vorher fehlten die zwei dynamischen
        // Stellen, 437 + 2 = 439 standen gegen 441). Eine Ziffer in einer
        // Anweisungsflaeche altert von selbst; eine Groessenordnung nicht.
        // Deshalb „rund zwei Dutzend" plus die Methode.
        //
        // 🔴 Die Methode ist ein JSX-BEWUSSTER Scan (Klammerung und
        // Zeilenumbrueche zaehlen mit), NICHT zeilenweises grep: steht `size=`
        // in der naechsten Zeile eines mehrzeiligen JSX-Elements, sieht es die
        // Zeile nicht — so entstand die Planzahl „~255 ohne `size`", die um eine
        // Groessenordnung danebenlag. Zeilenunabhaengig zaehlt nur die
        // Gesamtzahl; die Aufteilung mit/ohne `size` braucht den Block:
        //   grep -roh '<Chip\b' frontend/src --include='*.jsx' \
        //     --exclude='*.test.jsx' | wc -l
        // 🔴 `--exclude='*.test.jsx'` ist Pflicht, nicht Kosmetik: ohne es
        // liefert derselbe Befehl 476 — die 9 Treffer aus `*.test.jsx` fehlen
        // in der 467 daneben, und wer der Anweisung folgt, kann den
        // Unterschied nicht erklaeren (Regel-16-Befund W8, 2026-10-08).
        //
        // 🔴 24 px ist KEIN Verstoss gegen den Tippziel-Boden: WCAG 2.2 AA
        // 2.5.8 verlangt 24 px, und ein `small`-Chip liegt mit 24 px GENAU
        // darauf (nicht darueber — `pattern-library.md` behauptete bis zum
        // 2026-10-07, dieselbe Norm verlange 44 px; korrigiert). Fuer
        // KLICKBARE Chips gilt trotzdem der grobe Zeiger: ein Chip mit
        // `onClick`/`clickable`/`onDelete` ist ein Bedienelement, kein
        // Etikett, und bekommt denselben Boden wie die drei Knopf-Wurzeln.
        MuiChip: {
          defaultProps: { size: 'small' },
          styleOverrides: {
            root: {
              // `&.` statt eines eigenen Blocks: MUI setzt beide Klassen auf
              // dieselbe Wurzel (`Chip.js` vergibt `MuiChip-clickable` bzw.
              // `MuiChip-deletable` am Root), der Selektor braucht deshalb
              // keine Nachfahren-Ebene.
              // 🔴 `min-height` schlaegt `height` — dasselbe Wirkmittel wie in
              // `CONTROL_HEIGHT_SX`, kein zweites. Auch ein `height` im `sx`
              // eines Aufrufers (etwa `MovePillStrip.jsx`, das seine
              // Zug-Pillen ueber `height: { xs: 44, sm: 32 }` fuehrt) kann
              // den Boden damit nicht unterlaufen: `sx` gewinnt zwar gegen das
              // Theme, aber `height` und `min-height` sind zwei verschiedene
              // CSS-Eigenschaften — die kleinere `height` setzt sich gegen die
              // groessere `min-height` nicht durch.
              '&.MuiChip-clickable, &.MuiChip-deletable': {
                // 🔴 K-T1-Nachbesserung 3 (2026-10-09): transition-FREI, und
                // der `transform` steht in der EIGENEN `transition` — der Chip
                // fuehrt Farbe und Schatten selbst (`Chip.js:100`,
                // `create(['background-color', 'box-shadow'])`).
                ...TOUCH_TARGET_COARSE_ACTIVE,
                transition: mitDruck(
                  baseTheme.transitions.create(['background-color', 'box-shadow'], {
                    duration: DUR.ui,
                  }),
                ),
              },
              // 🔴 Dichte Kontexte nehmen ihn zurueck — dieselben drei wie bei
              // `MuiButton` (C1, Regel-15-Befund 1 vom 2026-10-08). Ohne diese
              // Zeile misst der klickbare Chip in der Bestellpositionen-Tabelle
              // (`PODetail.jsx`, `sx={{ height: 20 }}` in einer `<TableCell>`)
              // auf einem Tablet 44 px, waehrend der Knopf daneben bei 32 px
              // steht — eine Hoehendifferenz in EINER Zelle.
              // Nur `up('sm')` (Telefon behaelt den Boden). 🔴 `&` steht in JEDEM
              // Glied — die Form ist GEMESSEN, nicht gewaehlt (Regel-15-Befund,
              // 2026-10-08). Zwei Fassungen sind hier schon durchgefallen:
              //
              //  (a) OHNE `&`, als Nachfahren-Selektor geschrieben. Stylis stellt
              //      einem verschachtelten Selektor ohne `&` den Elternselektor
              //      VORAN — ausgeliefert wird dann
              //      `.css-…-MuiChip-root .MuiTableCell-root .MuiChip-root.MuiChip-clickable`.
              //      Der Chip ENTHAELT keine Tabellenzelle, die Regel trifft also
              //      NIE (gemessen unter Jest aus `document.styleSheets`, Chip mit
              //      `onClick` in `Table > TableCell`).
              //  (b) Der vorige Kommentar behauptete, die ausgeschriebene Form
              //      stelle die Spezifitaetsfrage nicht — widerlegt, sie stellt sie
              //      gar nicht erst, weil die Regel nicht greift.
              //
              // Mit `&` in jedem Glied setzt Stylis den Elternselektor an dessen
              // STELLE ein: `.MuiTableCell-root .css-…-MuiChip-root.MuiChip-clickable`.
              // Das sind Kontext (1) + Emotion-Klasse (1) + `.MuiChip-clickable` (1)
              // = 3 Klassen gegen den Boden `&.MuiChip-clickable` = 2 — die
              // Ruecknahme gewinnt unabhaengig von der Quelltextreihenfolge.
              '.MuiTableCell-root &.MuiChip-clickable, .MuiTablePagination-root &.MuiChip-clickable, .MuiToolbar-dense &.MuiChip-clickable, .MuiTableCell-root &.MuiChip-deletable, .MuiTablePagination-root &.MuiChip-deletable, .MuiToolbar-dense &.MuiChip-deletable': {
                [baseTheme.breakpoints.up('sm')]: { ...TOUCH_TARGET_COARSE_OFF },
              },
            },
          },
        },

        // ── Dialog-Aktionen ────────────────────────────────────────────
        //
        // 🔴 Knopfzeilen brechen um, statt Knoepfe abzuschneiden. Global, weil
        // es kein Einzelfall ist.
        //
        // WAS PASSIERT (gemessen 2026-09-05, Playwright, 375px, echtes Theme,
        // echter Komponentenbaum). Es gibt ZWEI Schadensbilder, und sie haengen
        // davon ab, ob ein Knopf `overflow: hidden` traegt:
        //
        //  (a) Knopf MIT `overflow: hidden` — der schrumpft unter seine
        //      Mindestbreite (die automatische Mindestgroesse eines Flex-Kindes
        //      ist null, sobald `overflow != visible`), und weil `MuiButton`
        //      unten `whiteSpace: 'nowrap'` setzt, wird der TEXT beschnitten.
        //      Betroffen sind vier Bausteine, die sich das fuer ihren
        //      Fortschrittsbalken selbst geben: `HoldButton`,
        //      `InlineConfirmButton`, `ConfirmOnSelectDropdown`,
        //      `ProgressButton`.
        //      Gemessen: „Hold to remove discontinuation" -> Knopf 139,4px bei
        //      178px Text, 41px weg, sichtbar „d to remove discontinua".
        //
        //  (b) Gewoehnlicher Knopf — der schrumpft NICHT, die Zeile laeuft
        //      stattdessen ueber. Und weil `DialogActions` rechtsbuendig ist,
        //      laeuft sie nach LINKS aus dem Dialog heraus.
        //      Gemessen an vier Knoepfen: „Cancel" lag bei x=-93px, also
        //      vollstaendig ausserhalb, und `scrollLeft` kommt dort nicht hin.
        //
        // 🔴 (b) IST NICHT UEBER `scrollWidth` ZU SEHEN. `scrollWidth` zaehlt
        // in LTR nur nach rechts; bei `justify-content: flex-end` faellt es
        // unter „passt" und „laeuft links raus" identisch aus. Genau daran ist
        // die erste Fassung dieser Notiz gescheitert — sie behauptete „kein
        // Ueberlauf" und stuetzte sich auf `scrollWidth === clientWidth`.
        // Richtig misst man KINDrechtecke gegen das PAPIERrechteck.
        //
        // Beide Bilder verschwinden mit `flexWrap`: der Knopf behaelt seine
        // natuerliche Breite und wandert in die naechste Zeile.
        //
        // REICHWEITE: **98** `DialogActions`-Bloecke, davon **11** mit drei
        // oder mehr Knopf-Tags.
        //
        // 🔴 Die Zahl kommt aus `scripts/count_dialog_actions.py` — nachrechnen
        // statt glauben. Hier standen nacheinander „46" und „102/14/18", beide
        // von den Gates als unreproduzierbar beanstandet. Eine Zahl ohne
        // mitgeliefertes Verfahren ist genau das: nicht nachpruefbar. Das
        // Skript nennt auch seine eigenen Grenzen (es zaehlt textuell, also
        // zaehlen Knoepfe hinter einer Bedingung mit).
        //
        // GRENZEN, ausdruecklich:
        //  · Verschachtelte Flex-Behaelter erreicht es NICHT. `Stack` in einem
        //    `DialogActions` braucht seinen eigenen Umbruch — siehe
        //    `RefundAndResolveDialog.jsx`.
        //  · Bei `justify-content: space-between` sitzt nach einem Umbruch je
        //    ein Element pro Zeile, was `flex-start` entspricht: der
        //    bestaetigende Knopf rutscht nach links. Die zwei betroffenen
        //    Dialoge schalten deshalb auf Mobile auf `flex-end`.
        //  · Ist ein EINZELNER Knopf breiter als die Zeile, hilft Umbruch
        //    allein nicht — er hat keine zweite Zeile zum Ausweichen. Deshalb
        //    steht unten zusaetzlich `whiteSpace: 'normal'`. Damit ist der Rest
        //    bei 320px gemessen NULL (vorher 26,4px).
        //  · `rma/MobileSheet.jsx` erreicht der Override NICHT: sein
        //    Mobile-Zweig rendert die Aktionen in eine gewoehnliche `Box`, nicht
        //    in `DialogActions`. Fuenf RMA-Bodenblaetter sind also aussen vor —
        //    vorbestehend, im Backlog. „Global" heisst hier: alle Dialoge, die
        //    `DialogActions` BENUTZEN.
        //
        // `rowGap` und nicht `gap`: `DialogActions` abstaendet seine Kinder
        // bereits ueber `marginLeft: 8` auf den Geschwistern — `gap` verdoppelte
        // den waagerechten Abstand. Rohe Pixelzahl wie beim `MuiTooltip`-
        // Override weiter unten; `styleOverrides` laeuft nicht durch
        // `theme.spacing`.
        MuiDialogActions: {
          styleOverrides: {
            root: {
              flexWrap: 'wrap',
              rowGap: 8,

              // 🔴 HIER STAND `'& .MuiButton-root': { whiteSpace: 'normal' }`
              // — ZURUECKGENOMMEN, vom Regel-14-Grill gefunden. Zwei Gruende,
              // beide gemessen:
              //
              //  1. Es ZERSTOERTE den `InlineConfirmButton`. Der baut seine
              //     Bestaetigungspille aus einem unsichtbaren Platzhalter und
              //     einer absolut positionierten Pille darueber, beide mit
              //     eigenem `sx={{ whiteSpace: 'nowrap' }}`. Eine Nachfahren-
              //     regel (`.css-…-MuiDialogActions-root .MuiButton-root`,
              //     Spezifitaet 0,2,0) sticht ein `sx` am Element (0,1,0) aus.
              //     Gemessen bei 900px, also AUF DEM DESKTOP: Pille 36,5px ->
              //     61px, Unterkante 16,5px unter dem Papier, in drei von vier
              //     Aufrufstellen.
              //
              //     ⚠️ Damit gilt „`sx` gewinnt immer" NICHT allgemein — es
              //     gilt nur fuer dieselbe Regel auf demselben Element. Am
              //     Behaelter gewinnt ein `sx={{flexWrap:'nowrap'}}`, am Knopf
              //     verliert ein `sx={{whiteSpace:'nowrap'}}`.
              //
              //  2. Der Nutzen war fast leer. Eine engere Variante
              //     (`flex-shrink: 0` statt `whiteSpace`) erreicht bei 320px
              //     dasselbe fuer JEDE echte Form ausser einer. Und die Form,
              //     mit der ich die Zeile begruendet hatte — „Remove invoice
              //     from this purchase order" — GIBT ES NICHT: die Beschriftung
              //     heisst `Remove invoice`. Ich hatte den rechtfertigenden
              //     Fall erfunden, zum dritten Mal in diesem Zweig.
              //
              // Die eine Beschriftung, die es wirklich braucht, traegt ihr
              // `whiteSpace` jetzt selbst — sichtbar an der Stelle, an der sie
              // zu lang ist. Ein globaler Override haette ihr genau das
              // Warnsignal genommen.
            },
          },
        },

        // 🖱️ Icon buttons: ensure 44px touch targets on mobile
        MuiIconButton: {
          defaultProps: { size: 'small' },
          styleOverrides: {
            root: {
              [baseTheme.breakpoints.down('sm')]: {
                minWidth: 44,
                minHeight: 44,
              },
              // Welle 4 (Karte 6), W1: eigener Wert fuer runde Knoepfe — `MuiIconButton`
              // traegt `borderRadius: '50%'`; mit `minHeight` allein wuerde er 28 × 44,
              // ein Oval. Deshalb BEIDE Achsen, und die Ruecknahme ebenso.
              // 🔴 K-T1-Nachbesserung 3 (2026-10-09): transition-FREI, der
              // `transform` steht in der EIGENEN `transition` — `MuiIconButton`
              // fuehrt `background-color` selbst (`IconButton.js:59`,
              // `duration.shortest` = 150 ms).
              ...TOUCH_TARGET_COARSE_QUADRAT_ACTIVE,
              transition: mitDruck(
                baseTheme.transitions.create('background-color', {
                  duration: baseTheme.transitions.duration.shortest,
                }),
              ),
              '.MuiTableCell-root &, .MuiTablePagination-root &, .MuiToolbar-dense &': {
                [baseTheme.breakpoints.down('sm')]: { minWidth: 44, minHeight: 44 },
                [baseTheme.breakpoints.up('sm')]: { ...TOUCH_TARGET_COARSE_QUADRAT_OFF },
              },
            },
          },
        },

        // W-02 — the two input components, plus FormControl (W-48, folded in
        // here on the Rule-16 gate's finding).
        //
        // `MuiFormControl` is the MORE COMPLETE instrument: `InputBase` reads
        // its size from FormControl context (InputBase.js:319-323), so this one
        // line also reaches the four raw `<OutlinedInput>` and three raw
        // `<InputBase>` sites that the per-component defaults leave at medium.
        // It is also what keeps a sizeless FormControl's InputLabel in step with
        // its control — without it the label's outlined transform stays at
        // `translate(14px, 16px)` while the control shrinks to the small one at
        // `translate(14px, 9px)`, sitting 7px low on nine forms.
        MuiFormControl: {
          defaultProps: { size: 'small' },
        },

        MuiTextField: {
          defaultProps: { size: 'small' },
        },

        // 🔴 `Select` does NOT read its size from FormControl in MUI 7.3.5 —
        // `Select.js:82-86` destructures `variant` and `error` only. This entry
        // is therefore necessary, not redundant with MuiFormControl above. An
        // earlier comment here claimed the opposite mechanism; corrected
        // 2026-09-19 after the gate checked it against the installed version.
        MuiSelect: {
          defaultProps: { size: 'small' },
        },

        // 📝 Inputs: force 16px on mobile to prevent iOS auto-zoom on focus
        MuiInputBase: {
          styleOverrides: {
            // 🔴 B1 (Rule-16 gate, 2026-09-19). The 44px touch floor below `sm`
            // was declared for `MuiButton` and `MuiIconButton` only. The two
            // input components had none, so W-02's `size: 'small'` took an
            // outlined field from 56px to **40px** on a phone — measured in
            // Chromium at a 390px viewport, three sizeless fields, all 40px,
            // while the button and icon button beside them measured 44px.
            // 40px is below Rule 22 §8's floor, so the floor is declared here
            // for the same reason and in the same shape as the other two.
            root: {
              [baseTheme.breakpoints.down('sm')]: {
                minHeight: 44,
              },
            },
            input: {
              [baseTheme.breakpoints.down('sm')]: {
                fontSize: 16,
              },
            },
          },
        },

        // 🧩 Cards: light padding tweak
        MuiCard: {
          styleOverrides: {
            root: { borderRadius: BUTTON_RADIUS, padding: 12 },
          },
        },

        // 🚨 Snackbar / flash messages: lift above the MobileBottomNav on
        // mobile so toasts don't hide behind the floating bottom strip.
        // 🔴 Die Hoehe stand hier bis zum 2026-10-07 als Literal `96` — und an
        //    einer zweiten Stelle in `App.js` —, waehrend sie an der dritten
        //    (den schwebenden Tabellenfuessen) FEHLTE. Sie kommt jetzt aus
        //    `theme/tokens.js` (`MOBILE_BOTTOM_NAV_CLEARANCE`, mit der
        //    Herleitung aus der Leiste selbst).
        // Above sm the default MUI position (24px) is fine.
        MuiSnackbar: {
          styleOverrides: {
            root: {
              '@media (max-width: 599.95px)': {
                bottom: MOBILE_BOTTOM_NAV_CLEARANCE,
              },
            },
          },
        },

        // 💀 Skeletons: rounded
        MuiSkeleton: {
          styleOverrides: {
            root: { borderRadius: 8 },
            text: { transform: 'scale(1,1)' },
          },
        },

        // 🪟 Backdrop: Apple-glass blur on all overlays (Dialog, Drawer, Modal…)
        MuiBackdrop: {
          styleOverrides: {
            root: {
              backgroundColor: mode === 'light'
                ? 'rgba(0, 0, 0, 0.28)'
                : 'rgba(0, 0, 0, 0.55)',
              backdropFilter: 'blur(8px) saturate(160%)',
              WebkitBackdropFilter: 'blur(8px) saturate(160%)',
              // Invisible backdrop (e.g. Tooltip/Popover) keeps zero opacity
              '&.MuiBackdrop-invisible': {
                backgroundColor: 'transparent',
                backdropFilter: 'none',
                WebkitBackdropFilter: 'none',
              },
            },
          },
        },

        // 🔇 E-3 (UI2 Teil 9, Z-1a): waehrend eines bestaetigten Ausfalls
        // schweigen die Fehler-Alerts IM SEITENINHALT. Der Erreichbarkeits-
        // Waechter (`frontend/src/api/serviceOutage.js`) setzt dafuer die Klasse
        // `pog-ausfall` an `document.body`; diese EINE Regel blendet die drei
        // `error`-Varianten aus, solange sie steht — ohne eine der 109 Flaechen
        // anzufassen.
        //
        // 🔴 Der Selektor braucht `main`: MUI portalt Dialoge/Drawer/Popover/
        // Modal an `document.body`, also AUSSERHALB von `main`
        // (`components/SidebarAndHeaderBar.jsx:2402` Desktop, `:2878` Mobil).
        // Ohne `main` verschwaende auch ein Fehler in einem Dialog — und der ist
        // oft der einzige Ort, an dem die betroffene Handlung noch sichtbar ist.
        //
        // 🔴 `display:none` LOESCHT nichts: nach der Erholung faellt die Klasse,
        // und genau dieselbe Flaeche steht wieder da. Deshalb ist es vertretbar,
        // dass die Flächen ohne Netzaufruf mitverschwinden (§2.6, W5).
        //
        // Die drei Variantenschluessel sind MUI-v7-Schluessel; die Datei trug
        // bisher KEINEN `MuiAlert`-Eintrag und keinen `body.`-Selektor.
        MuiAlert: {
          styleOverrides: {
            standardError: { 'body.pog-ausfall main &': { display: 'none' } },
            filledError:   { 'body.pog-ausfall main &': { display: 'none' } },
            outlinedError: { 'body.pog-ausfall main &': { display: 'none' } },
          },
        },
      },
    })
  );

  // 3️⃣ Make fonts responsive (keeps default typography scaling)
  theme = responsiveFontSizes(theme);

  // 4️⃣ 🔴 Den `create`-Griff nachziehen (K-T1-Nachbesserung, 2026-10-09).
  //
  // `transitions.duration` allein genuegt NICHT, und das ist gemessen, nicht
  // abgeleitet: `transitions.duration.standard` stand nach der Zeile oben auf
  // 200, waehrend `transitions.create('height')` weiterhin „height 300ms …"
  // lieferte.
  //
  // Der Grund steht in MUI selbst (`@mui/material/styles/createTransitions.js`):
  // das Ergebnis ist `{ getAutoHeightDuration, create, ...inputTransitions, … }`
  // — der Spread steht NACH `create`. Traegt die Eingabe schon einen `create`,
  // dann GEWINNT der alte. Und sie traegt einen: `deepmerge(baseTheme, …) oben`
  // mischt `baseTheme.transitions` tief, also bleibt `baseTheme`s `create`
  // stehen — gebunden an MUI's STATISCHE Dauern (standard 300), nicht an `DUR`.
  //
  // Wirkung im Browser, und sie war der Befund vom 2026-10-09: `Collapse`
  // traegt seinen Uebergang in der KLASSE (`Collapse.js:47`,
  // `theme.transitions.create('height')`). Jeder aufklappende Bereich, der
  // gerade NICHT laeuft, mass dort 0,3 s statt 0,2 s — und dieselbe Zeile
  // speist jede weitere `create`-Stelle im Baum (Button-, Tab-, Dialog-,
  // Drawer-Uebergaenge). Die Abnahme 2 des Plans („Collapse laeuft in
  // `DUR.ui`") war damit nur zur HAELFTE eingeloest: waehrend eines Uebergangs
  // 200 ms (aus `MuiCollapse.defaultProps.timeout`), im Ruhezustand 300 ms.
  //
  // *Widerleger:* ein `create`-Aufruf im Baum, der MUI's statische Dauer
  // liefern SOLL — es gibt keinen; die Haus-Dauer ist `DUR.ui`, und genau das
  // sagt die Zeile `duration` darueber.
  theme.transitions.create = createTransitions({
    duration: theme.transitions.duration,
    easing: theme.transitions.easing,
  }).create;

  return theme;
}

/**
 * Der eigene Uebergang einer MUI-Wurzel PLUS dem Druck-`transform`.
 *
 * 🔴 K-T1-Nachbesserung 3 (2026-10-09) — die Hausform fuer jede Flaeche, die
 * ihren Uebergang selbst fuehrt (Muster `MobileBottomNav.jsx:197`). `transform`
 * steht in MUI's Vorgabe-Uebergaengen NICHT drin (`Button` fuehrt
 * Farbe/Schatten/Rahmen), er muss also mit. Und er darf NICHT ueber einen
 * zweiten `transition` im Medienblock kommen: `transition` ist keine additive
 * Eigenschaft — der Eintrag ERSETZT die ganze Liste, und die Wurzel verloere
 * auf dem groben Zeiger ihren Farb- und Schattenuebergang (R16 W2 / R15 W1,
 * gemessen an `MuiButton`/`MuiIconButton`).
 *
 * `eigenerUebergang` ist der von MUI erzeugte String
 * (`baseTheme.transitions.create(...)`); die Reihenfolge (Farbe zuerst,
 * `transform` zuletzt) ist die der Unterleiste.
 *
 * ⚠️ Steht ABSICHTLICH am Dateiende: `buildTheme` ist eine Funktionsdeklaration
 * und wird erst NACH der Modulauswertung aufgerufen, hier ist die Konstante also
 * laengst initialisiert — und die Zeilen oberhalb bleiben unveraendert (jede
 * Verschiebung dort zoege die Verweise der Doku nach).
 */
const mitDruck = (eigenerUebergang) => `${eigenerUebergang}, ${PRESS_TRANSITION}`;

/**
 * v2ToneHover — der Hover einer UMRANDETEN Stelle ODER eines Text-Knopfes im
 * SELBEN Ton (K-3).
 *
 * WARUM ES IHN GIBT
 * -----------------
 * `variant="outlined"` leitet Rand UND Toenung des Hovers aus
 * `palette[ownerState.color]` ab. K-3 nimmt den `color`-Prop von 29 umrandeten
 * Stellen — er lieferte `palette.warning.main` / `.info.main` als SCHRIFT auf
 * dem Papier (hell 3,113:1 / 3,857:1, beide unter 4,5:1, WCAG 1.4.3). Ohne ihn
 * faellt `ownerState.color` auf `primary` zurueck: der Knopf zeigt beim Zeigen
 * einen blauen Rand und eine blaue Toenung, waehrend seine Schrift warn bleibt.
 *
 * Die Hausform setzt beides im Ton nach — derselbe Rand wie im Ruhezustand und
 * die gewohnte MUI-Toenung (`action.hoverOpacity`), nur im v2-Ton. Am
 * `text`-Knopf traegt allein die Toenung; der Rand ist dort wirkungslos
 * (`MuiButtonBase` traegt `border: 0`), und er wird trotzdem gesetzt, damit
 * keine Stelle eine zweite Form braucht. Verpackt in `hoverOnly`, weil ein
 * Touchgeraet `:hover` beim Tippen anwendet und STEHEN laesst
 * (`tokens.js::hoverOnly`).
 *
 * @param t     a MUI theme
 * @param key   'ok' | 'warn' | 'err' | 'info' | 'agent' | 'ink'
 *
 * @useWhen     eine BEDIENBARE Stelle traegt ihren Ton aus `v2Tone` statt aus
 *              `palette.<ton>.main` — umrandet (`Button variant="outlined"`,
 *              klickbarer `Chip`) oder `text`-Knopf (`ops/LsmWatcher.jsx`; dort
 *              ist der Rand wirkungslos, die Toenung traegt).
 * @dontUseFor  gefuellte Flaechen (`contained`, gefuellter Chip — dort ist der
 *              Ton die FLAECHE) und nicht bedienbare Chips (RMAInbox
 *              `aktionTonSx`) — auch dann nicht, wenn sie in einer klickbaren
 *              Zeile oder `CardActionArea` liegen: dort ist der TRAEGER die
 *              Flaeche, die auf den Klick antwortet, und ein eigener Hover
 *              taeuscht ein zweites Bedienelement vor.
 *
 * ⚠️ Steht ABSICHTLICH am Dateiende, wie `mitDruck` darueber: die Zeilen
 * oberhalb bleiben unveraendert, und eine Einfuegung neben `v2Tone` zoege die
 * Zeilenverweise der Doku nach (`check_verweis_grundlinie.py`).
 */
export const v2ToneHover = (t, key) => hoverOnly({
  borderColor: v2Tone(t, key).main,
  backgroundColor: alpha(v2Tone(t, key).main, t.palette.action.hoverOpacity),
});
